import { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, RefreshCw, Trash2, ExternalLink, Rss, X, List, LayoutList } from 'lucide-react'
import { rssAPI, widgetAPI } from '../../services/api'
import toast from 'react-hot-toast'
import { formatDistanceToNow } from 'date-fns'

interface RssWidgetProps {
  widget: any
  isExpanded: boolean
}

export default function RssWidget({ widget, isExpanded }: RssWidgetProps) {
  const [showAddFeed, setShowAddFeed] = useState(false)
  const [newFeedUrl, setNewFeedUrl] = useState('')
  const [subscribing, setSubscribing] = useState(false)
  const [selectedFeedId, setSelectedFeedId] = useState<number | null>(null)
  const [refreshingFeedId, setRefreshingFeedId] = useState<number | null>(null)
  const [condensedView, setCondensedView] = useState(widget?.config?.condensedView ?? false)
  const queryClient = useQueryClient()

  // Load saved preference on mount
  useEffect(() => {
    if (widget?.config?.condensedView !== undefined) {
      setCondensedView(widget.config.condensedView)
    }
  }, [widget?.config?.condensedView])

  // Save preference to backend
  const saveViewPreference = async (isCondensed: boolean) => {
    try {
      const newConfig = {
        ...widget.config,
        condensedView: isCondensed
      }
      await widgetAPI.update(widget.id, {
        config_data: JSON.stringify(newConfig)
      })
      // Update the widget in the query cache
      queryClient.invalidateQueries({ queryKey: ['widgets'] })
    } catch (error) {
      console.error('Failed to save view preference:', error)
      // Don't show error toast - silent failure for better UX
    }
  }

  // Toggle view and save preference
  const toggleView = () => {
    const newValue = !condensedView
    setCondensedView(newValue)
    saveViewPreference(newValue)
  }

  // Fetch user's RSS feeds
  const { data: feeds } = useQuery({
    queryKey: ['rss-feeds'],
    queryFn: async () => {
      const { data } = await rssAPI.getFeeds()
      return data
    },
  })

  // Fetch all feed items
  const { data: allItems } = useQuery({
    queryKey: ['rss-all-items'],
    queryFn: async () => {
      const { data } = await rssAPI.getAllItems()
      return data
    },
  })

  // Fetch specific feed items
  const { data: feedData } = useQuery({
    queryKey: ['rss-feed-items', selectedFeedId],
    queryFn: async () => {
      if (!selectedFeedId) return null
      const { data } = await rssAPI.getFeedItems(selectedFeedId)
      return data
    },
    enabled: !!selectedFeedId,
  })

  const handleSubscribe = async () => {
    if (!newFeedUrl.trim()) {
      toast.error('Please enter a feed URL')
      return
    }

    setSubscribing(true)
    try {
      await rssAPI.subscribe(newFeedUrl)
      queryClient.invalidateQueries({ queryKey: ['rss-feeds'] })
      queryClient.invalidateQueries({ queryKey: ['rss-all-items'] })
      toast.success('Subscribed to feed!')
      setNewFeedUrl('')
      setShowAddFeed(false)
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to subscribe to feed')
    } finally {
      setSubscribing(false)
    }
  }

  const handleRefresh = async (feedId: number) => {
    setRefreshingFeedId(feedId)
    try {
      const { data } = await rssAPI.refresh(feedId)
      queryClient.invalidateQueries({ queryKey: ['rss-feeds'] })
      queryClient.invalidateQueries({ queryKey: ['rss-all-items'] })
      queryClient.invalidateQueries({ queryKey: ['rss-feed-items', feedId] })
      toast.success(`Refreshed! ${data.new_items} new items`)
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to refresh feed')
    } finally {
      setRefreshingFeedId(null)
    }
  }

  const handleUnsubscribe = async (feedId: number, feedTitle: string) => {
    if (!confirm(`Unsubscribe from "${feedTitle}"?`)) return

    try {
      await rssAPI.unsubscribe(feedId)
      queryClient.invalidateQueries({ queryKey: ['rss-feeds'] })
      queryClient.invalidateQueries({ queryKey: ['rss-all-items'] })
      if (selectedFeedId === feedId) {
        setSelectedFeedId(null)
      }
      toast.success('Unsubscribed')
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to unsubscribe')
    }
  }

  const renderFeedItem = (item: any) => {
    if (condensedView) {
      // Condensed view: icon, date, and name in 2 lines
      return (
        <a
          key={item.id}
          href={item.link}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 p-2 border border-gray-200 rounded-lg hover:border-primary-300 hover:shadow-sm transition bg-white group"
        >
          {item.favicon_url && (
            <img
              src={item.favicon_url}
              alt=""
              className="w-4 h-4 flex-shrink-0"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm text-gray-900 group-hover:text-primary-600 transition truncate">
              {item.title}
            </div>
            <div className="text-xs text-gray-500 truncate">
              {item.published_at && (
                <span>{formatDistanceToNow(new Date(item.published_at), { addSuffix: true })}</span>
              )}
              {item.feed_title && item.published_at && <span className="mx-1">•</span>}
              {item.feed_title && <span>{item.feed_title}</span>}
            </div>
          </div>
          <ExternalLink size={14} className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition text-gray-400" />
        </a>
      )
    }

    // Full view
    return (
      <div
        key={item.id}
        className="p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-sm transition bg-white"
      >
        <div className="flex items-start gap-3">
          {item.favicon_url && (
            <img
              src={item.favicon_url}
              alt=""
              className="w-4 h-4 mt-1 flex-shrink-0"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
          )}
          <div className="flex-1 min-w-0">
            <a
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-gray-900 hover:text-blue-600 transition flex items-center gap-1 group"
            >
              <span className="truncate">{item.title}</span>
              <ExternalLink size={12} className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition" />
            </a>
            
            {item.feed_title && (
              <div className="text-xs text-gray-500 mt-0.5">{item.feed_title}</div>
            )}
            
            {/* Show image when expanded and not in compact view */}
            {isExpanded && item.image_url && (
              <img 
                src={item.image_url} 
                alt={item.title}
                className="w-full h-48 object-cover rounded-lg mt-2"
                onError={(e) => {
                  // Hide image if it fails to load
                  e.currentTarget.style.display = 'none'
                }}
              />
            )}
            
            {item.description && (
              <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                {item.description.replace(/<[^>]*>/g, '')}
              </p>
            )}
            
            <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
              {item.author && <span>by {item.author}</span>}
              {item.published_at && (
                <span>{formatDistanceToNow(new Date(item.published_at), { addSuffix: true })}</span>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (showAddFeed) {
    return (
      <div className="h-full flex flex-col p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Subscribe to RSS Feed</h3>
          <button
            onClick={() => setShowAddFeed(false)}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Feed URL
            </label>
            <input
              type="url"
              value={newFeedUrl}
              onChange={(e) => setNewFeedUrl(e.target.value)}
              placeholder="https://example.com/feed.xml"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
            />
          </div>

          <div className="bg-primary-50 border border-primary-200 rounded-lg p-3">
            <p className="text-sm text-primary-800">
              <strong>Popular feeds to try:</strong>
            </p>
            <ul className="text-xs text-blue-700 mt-2 space-y-1">
              <li>• TechCrunch: https://techcrunch.com/feed/</li>
              <li>• Hacker News: https://news.ycombinator.com/rss</li>
              <li>• The Verge: https://www.theverge.com/rss/index.xml</li>
              <li>• Ars Technica: https://feeds.arstechnica.com/arstechnica/index</li>
            </ul>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleSubscribe}
              disabled={subscribing}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition disabled:opacity-50"
            >
              {subscribing ? 'Subscribing...' : 'Subscribe'}
            </button>
            <button
              onClick={() => setShowAddFeed(false)}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (selectedFeedId && feedData) {
    return (
      <div className="h-full flex flex-col">
        <div className="p-3 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {feedData.feed.favicon_url && (
              <img
                src={feedData.feed.favicon_url}
                alt=""
                className="w-5 h-5 flex-shrink-0"
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                }}
              />
            )}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 truncate">{feedData.feed.feed_title}</h3>
              {feedData.feed.feed_description && (
                <p className="text-xs text-gray-600 truncate">{feedData.feed.feed_description}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 ml-2">
            <button
              onClick={toggleView}
              className="p-1 hover:bg-gray-200 rounded flex-shrink-0"
              title={condensedView ? 'Switch to full view' : 'Switch to condensed view'}
            >
              {condensedView ? <LayoutList size={16} /> : <List size={16} />}
            </button>
            <button
              onClick={() => setSelectedFeedId(null)}
              className="p-1 hover:bg-gray-200 rounded flex-shrink-0"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {feedData.items && feedData.items.length > 0 ? (
            feedData.items.map((item: any) => renderFeedItem(item))
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Rss size={48} className="mx-auto mb-2 opacity-30" />
              <p>No items in this feed yet</p>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      <div className="p-3 border-b border-gray-200 flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">RSS Feeds</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleView}
            className="flex items-center gap-1 px-2 py-1 text-gray-600 hover:bg-gray-100 rounded transition text-sm"
            title={condensedView ? 'Switch to full view' : 'Switch to condensed view'}
          >
            {condensedView ? <LayoutList size={16} /> : <List size={16} />}
          </button>
          <button
            onClick={() => setShowAddFeed(true)}
            className="flex items-center gap-1 px-3 py-1 bg-primary-600 text-white rounded hover:bg-primary-700 transition text-sm"
          >
            <Plus size={14} />
            Add Feed
          </button>
        </div>
      </div>

      {feeds && feeds.length > 0 ? (
        <div className={isExpanded ? "flex flex-1 overflow-hidden" : "flex-1 overflow-y-auto"}>
          {isExpanded && (
            <div className="w-64 border-r border-gray-200 overflow-y-auto bg-gray-50">
              <div className="p-2">
                <button
                  onClick={() => setSelectedFeedId(null)}
                  className={`w-full text-left px-3 py-2 rounded mb-1 transition ${
                    !selectedFeedId ? 'bg-primary-100 text-primary-700 font-semibold' : 'hover:bg-gray-100'
                  }`}
                >
                  All Items
                </button>
                {feeds.map((feed: any) => (
                  <div key={feed.id} className="group relative">
                    <button
                      onClick={() => setSelectedFeedId(feed.id)}
                      className={`w-full text-left px-3 py-2 rounded mb-1 transition ${
                        selectedFeedId === feed.id ? 'bg-primary-100 text-primary-700 font-semibold' : 'hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {feed.favicon_url && (
                          <img
                            src={feed.favicon_url}
                            alt=""
                            className="w-4 h-4 flex-shrink-0"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none'
                            }}
                          />
                        )}
                        <span className="truncate text-sm">{feed.feed_title}</span>
                      </div>
                    </button>
                    <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition flex gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRefresh(feed.id)
                        }}
                        disabled={refreshingFeedId === feed.id}
                        className="p-1 bg-white hover:bg-primary-50 rounded shadow-sm"
                        title="Refresh"
                      >
                        <RefreshCw size={12} className={refreshingFeedId === feed.id ? 'animate-spin' : ''} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleUnsubscribe(feed.id, feed.feed_title)
                        }}
                        className="p-1 bg-white hover:bg-red-50 text-red-600 rounded shadow-sm"
                        title="Unsubscribe"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {allItems && allItems.length > 0 ? (
              allItems.map((item: any) => renderFeedItem(item))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Rss size={48} className="mx-auto mb-2 opacity-30" />
                <p>No feed items yet</p>
                <p className="text-sm mt-1">Try refreshing your feeds</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <Rss size={64} className="mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No RSS Feeds Yet</h3>
            <p className="text-gray-600 mb-4">Subscribe to RSS feeds to stay updated</p>
            <button
              onClick={() => setShowAddFeed(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
            >
              <Plus size={18} />
              Add Your First Feed
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

